import { alpha, ButtonBase, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../localization/translations"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"

const useOptionStyles = makeStyles()((theme) => ({
    option: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "1.25rem 1rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: `0 .125rem .375rem 0 ${alpha(theme.palette.common.black, 0.08)}`,
        background: theme.palette.tailwind.purple[50],
        textAlign: "left",
        cursor: "pointer",
        transition: theme.transitions.create("background-color", {
            duration: theme.transitions.duration.short,
        }),
        "&:hover": {
            background: theme.palette.tailwind.purple[100],
        },
    },
    title: {
        color: theme.palette.tailwind.purple[800],
    },
    subtitle: {
        color: theme.palette.tailwind.slate[600],
    },
    choose: {
        flexShrink: 0,
        color: theme.palette.tailwind.purple[800],
    },
}))

type OptionProps = Readonly<{
    titleId: TranslationKey
    title: string
    subtitleId: TranslationKey
    subtitle: string
    onClick: () => void
}>

function AiOverwriteOption({ titleId, title, subtitleId, subtitle, onClick }: OptionProps) {
    const { classes } = useOptionStyles()

    return (
        <ButtonBase className={classes.option} onClick={onClick}>
            <Stack component="span" spacing=".25rem">
                <LocalizedStrict id={titleId}>
                    <Typography component="span" variant="subtitle1" className={classes.title}>
                        {title}
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict id={subtitleId}>
                    <Typography variant="caption" className={classes.subtitle}>
                        {subtitle}
                    </Typography>
                </LocalizedStrict>
            </Stack>
            <LocalizedStrict id="merchant-week-menu-ai-overwrite-choose-button">
                <Typography component="span" variant="subtitle1" className={classes.choose}>
                    Choose
                </Typography>
            </LocalizedStrict>
        </ButtonBase>
    )
}

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    subtitle: {
        color: theme.palette.tailwind.gray[500],
    },
    options: {
        padding: "1rem",
    },
}))

type Props = Readonly<{
    open: boolean
    onChoose: (overwriteFilled: boolean) => void
    onClose: () => void
}>

/** Quick AI generation on a week that already has dishes: keep them or replace everything. */
export function AiOverwriteDialog({ open, onChoose, onClose }: Props) {
    const { classes } = useStyles()

    return (
        <LocalizedStrict id="merchant-week-menu-ai-overwrite-dialog" attrs={{ dialogTitle: true }}>
            <StandardDialog open={open} dialogTitle="This menu already has dishes" onClose={onClose}>
                <Stack spacing="1rem" className={classes.content}>
                    <LocalizedStrict id="merchant-week-menu-ai-overwrite-subtitle">
                        <Typography variant="body2" align="center" className={classes.subtitle}>
                            How should the AI handle the days that are already filled?
                        </Typography>
                    </LocalizedStrict>

                    <Stack spacing="1rem" className={classes.options}>
                        <AiOverwriteOption
                            titleId="merchant-week-menu-ai-overwrite-fill-title"
                            title="Fill empty slots only"
                            subtitleId="merchant-week-menu-ai-overwrite-fill-subtitle"
                            subtitle="Keep the dishes you already wrote"
                            onClick={() => onChoose(false)}
                        />
                        <AiOverwriteOption
                            titleId="merchant-week-menu-ai-overwrite-replace-title"
                            title="Replace all dishes"
                            subtitleId="merchant-week-menu-ai-overwrite-replace-subtitle"
                            subtitle="Overwrite the whole week"
                            onClick={() => onChoose(true)}
                        />
                    </Stack>

                    <Stack direction="row" justifyContent="center">
                        <LocalizedStrict id="merchant-week-menu-ai-overwrite-cancel-button">
                            <StandardButton fullWidth={false} variant="outlined" color="black" onClick={onClose}>
                                Cancel
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
